import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { MonitorUp, Loader2, Search, Send, X, History, Circle } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { api } from '../context/AuthContext';
import Header from '../components/Header';
import { useCobrowseAgent } from '../components/cobrowse/useCobrowseAgent';
import { NetzBalken } from '../components/cobrowse/Netzanzeige';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const dauer = (a, b) => (a && b ? `${Math.max(1, Math.round((new Date(b) - new Date(a)) / 60000))} Min.` : '—');
const STATUS = { aktiv: 'läuft', beendet: 'beendet', eingeladen: 'eingeladen', abgelehnt: 'abgelehnt', abgelaufen: 'abgelaufen' };

const VORLAGEN = ['Hier klicken', 'Hier hochladen', 'Hier ausfüllen', 'Hier unterschreiben'];
const MarkNotiz = ({ pos, onOk, onAbbruch }) => {
  const [text, setText] = useState('');
  return (
    <form onSubmit={(e) => { e.preventDefault(); onOk(text); }} onKeyDown={(e) => e.key === 'Escape' && onAbbruch()}
      className="absolute z-20 w-72 bg-white rounded-xl shadow-2xl border border-[#b8893d]/50 p-3" style={{ left: pos.left, top: pos.top }} data-testid="cobrowse-mark-notiz">
      <p className="text-[10px] tracking-[0.2em] uppercase text-[#b8893d] mb-1.5">Notiz zur Markierung</p>
      <input autoFocus value={text} onChange={(e) => setText(e.target.value.slice(0, 80))} placeholder="z. B. Hier hochladen (optional)"
        className="w-full h-9 px-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#b8893d]" data-testid="cobrowse-mark-text" />
      <div className="flex flex-wrap gap-1 mt-2">{VORLAGEN.map((v) => (
        <button key={v} type="button" onClick={() => onOk(v)} className="px-2 py-0.5 rounded-full border border-gray-200 text-[11px] text-[#1B3A5C] hover:border-[#b8893d]" data-testid={`cobrowse-mark-vorlage-${v.split(' ')[1]}`}>{v}</button>))}</div>
      <div className="flex gap-2 mt-3">
        <button type="submit" className="flex-1 h-8 rounded-lg bg-[#1B3A5C] text-white text-xs" data-testid="cobrowse-mark-senden">{text.trim() ? 'Markieren mit Notiz' : 'Nur markieren'}</button>
        <button type="button" onClick={onAbbruch} className="h-8 px-3 rounded-lg border border-gray-300 text-xs text-[#1B3A5C]" data-testid="cobrowse-mark-abbrechen">Abbrechen</button>
      </div>
    </form>
  );
};

const Ansicht = ({ sitzung, onEnde }) => {
  const ende = useCallback((von, grund) => { toast.message(grund || (von === 'mandant' ? 'Der Mandant hat die Sitzung beendet' : 'Sitzung beendet')); onEnde(); }, [onEnde]);
  const { root, verbunden, groesse, seite, netz, markWunsch, markieren, markAbbrechen } = useCobrowseAgent(sitzung, ende);
  const box = useRef(null);
  const [breite, setBreite] = useState(1000);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setBreite(e.contentRect.width));
    if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const skala = groesse ? Math.min(1, breite / groesse.w) : 1;
  const beenden = async () => { await api.post(`/cobrowse/sitzungen/${sitzung.session_id}/beenden`, null, { params: { token: sitzung.agent_token } }).catch(() => {}); onEnde(); };
  return (
    <section className="bg-[#0B1724] rounded-2xl p-3 sm:p-4 shadow-2xl" data-testid="cobrowse-ansicht">
      <div className="flex flex-wrap items-center gap-3 text-xs text-white/80 mb-3 px-1">
        <Circle className={`w-2.5 h-2.5 ${verbunden ? 'fill-emerald-400 text-emerald-400' : 'fill-[#e0b56a] text-[#e0b56a] animate-pulse'}`} />
        <span data-testid="cobrowse-agent-status">{verbunden ? <>Verbunden mit <b className="text-white">{sitzung.client_name}</b></> : `Warte auf ${sitzung.client_name || 'Mandant'} …`}</span>
        {verbunden && <NetzBalken netz={netz} />}
        {seite && <span className="font-mono text-[11px] text-[#e0b56a] truncate max-w-[40ch]" data-testid="cobrowse-agent-seite">{seite}</span>}
        {groesse && <span className="text-white/40">{groesse.w}×{groesse.h}</span>}
        <span className="text-white/40 hidden md:inline" data-testid="cobrowse-hilfe">Klicken, scrollen und tippen wirkt direkt beim Mandanten · <b className="text-[#e0b56a] font-normal">Alt + Klick markiert eine Stelle</b> · graue Flächen sind geschützt</span>
        <button type="button" onClick={beenden} className="ml-auto h-8 px-4 rounded-full bg-rose-600 hover:bg-rose-700 text-white transition-colors" data-testid="cobrowse-agent-beenden">Sitzung beenden</button>
      </div>
      <div ref={box} className="relative w-full overflow-hidden rounded-xl bg-white/5" style={{ height: groesse ? groesse.h * skala : 420 }}>
        {!groesse && <div className="h-[420px] flex flex-col items-center justify-center text-white/50 text-sm gap-2"><Loader2 className="w-6 h-6 animate-spin text-[#e0b56a]" /> Bildschirm wird übertragen, sobald der Mandant verbunden ist</div>}
        <div ref={root} className="cobrowse-root origin-top-left" style={{ transform: `scale(${skala})`, width: groesse?.w, height: groesse?.h, marginLeft: groesse ? Math.max(0, (breite - groesse.w * skala) / 2) : 0 }} data-testid="cobrowse-replay" />
        {markWunsch && groesse && (
          <MarkNotiz pos={{ left: Math.min(Math.max(0, (breite - groesse.w * skala) / 2) + markWunsch.x * skala, breite - 300), top: Math.max(0, Math.min(markWunsch.y * skala + 12, groesse.h * skala - 190)) }} onOk={markieren} onAbbruch={markAbbrechen} />
        )}
      </div>
    </section>
  );
};

const CodeFormular = ({ onStart }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const los = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.post('/cobrowse/beitreten', { code }); onStart(r.data); } catch (err) { toast.error(err?.response?.data?.detail || 'Code ungültig'); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={los} className="bg-white border border-gray-200 p-5" data-testid="cobrowse-code-form">
      <h3 className="text-[10px] tracking-[0.2em] uppercase text-[#b8893d]">Code vom Mandanten</h3>
      <p className="text-xs text-gray-500 mt-1">Der Mandant klickt auf der Website oder im Portal auf „Bildschirm teilen“ und nennt Ihnen den Code.</p>
      <div className="flex gap-2 mt-3">
        <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" placeholder="000000"
          className="flex-1 min-w-0 h-11 px-3 font-mono text-xl tracking-[0.4em] border border-gray-300 rounded-[10px] focus:outline-none focus:border-[#b8893d]" data-testid="cobrowse-code-input" />
        <button type="submit" disabled={code.length !== 6 || busy} className="h-11 px-5 rounded-[10px] bg-[#1B3A5C] text-white text-sm disabled:opacity-40 inline-flex items-center gap-2" data-testid="cobrowse-code-verbinden">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MonitorUp className="w-4 h-4" />} Verbinden
        </button>
      </div>
    </form>
  );
};

const Einladen = ({ onStart }) => {
  const [params] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [treffer, setTreffer] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setTreffer([]); return undefined; }
    const t = setTimeout(() => api.get('/cobrowse/mandanten', { params: { q } }).then((r) => setTreffer(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const einladen = async (u) => {
    try { const r = await api.post('/cobrowse/einladen', { user_id: u.user_id }); toast.success(`Einladung an ${u.name || u.email} gesendet – erscheint im Portal`); setQ(''); onStart(r.data); } catch (e) { toast.error(e?.response?.data?.detail || 'Einladung fehlgeschlagen'); }
  };
  return (
    <div className="bg-white border border-gray-200 p-5" data-testid="cobrowse-einladen">
      <h3 className="text-[10px] tracking-[0.2em] uppercase text-[#b8893d]">Eingeloggten Mandanten einladen</h3>
      <p className="text-xs text-gray-500 mt-1">Die Einladung erscheint im Mandantenportal, der Mandant muss zustimmen.</p>
      <div className="relative mt-3">
        <Search className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name oder E-Mail des Portalkontos" className="w-full h-11 pl-9 pr-8 border border-gray-300 rounded-[10px] text-sm focus:outline-none focus:border-[#b8893d]" data-testid="cobrowse-einladen-suche" />
        {q && <button type="button" onClick={() => setQ('')} className="absolute right-3 top-3.5 text-gray-400" aria-label="Leeren"><X className="w-4 h-4" /></button>}
      </div>
      {treffer.length > 0 && (
        <ul className="mt-2 divide-y divide-gray-100 border border-gray-100 rounded-[10px]">{treffer.map((u) => (
          <li key={u.user_id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <span className="min-w-0 flex-1 truncate text-[#1B3A5C]">{u.name || '—'} <span className="text-gray-400 text-xs">{u.email}</span></span>
            <button type="button" onClick={() => einladen(u)} className="shrink-0 inline-flex items-center gap-1 text-xs text-[#b8893d] hover:text-[#1B3A5C]" data-testid={`cobrowse-einladen-${u.user_id}`}><Send className="w-3.5 h-3.5" /> Einladen</button>
          </li>))}</ul>
      )}
    </div>
  );
};

const Zuordnung = ({ s, onDone }) => {
  const [offen, setOffen] = useState(false);
  const [nr, setNr] = useState(s.mandanten_nr || '');
  const [notiz, setNotiz] = useState(s.notiz || '');
  const speichern = async () => {
    try { await api.post(`/cobrowse/protokoll/${s.id}/zuordnen`, { mandanten_nr: nr, notiz }); setOffen(false); onDone(); toast.success('Gespeichert – erscheint im Zeitstrahl des Mandanten'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };
  if (!offen) return (
    <span className="inline-flex items-center gap-2">
      {s.mandanten_nr && s.joined_at ? <Link to={`/kanzlei/mandanten/${s.mandanten_nr}`} className="font-mono text-[#b8893d] hover:underline" data-testid={`cobrowse-zuordnung-${s.id}`}>M-{s.mandanten_nr}</Link> : <span className="text-gray-400">—</span>}
      {s.joined_at && !['eingeladen', 'abgelehnt'].includes(s.status) && <button type="button" onClick={() => setOffen(true)} className="text-[10px] text-[#1B3A5C] underline underline-offset-2" data-testid={`cobrowse-zuordnen-${s.id}`}>{s.mandanten_nr ? 'Notiz' : 'Zuordnen'}</button>}
    </span>
  );
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid={`cobrowse-zuordnen-form-${s.id}`}>
      <input value={nr} onChange={(e) => setNr(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Mandanten-Nr." className="w-24 h-7 px-2 border border-gray-300 rounded-md font-mono" data-testid={`cobrowse-zuordnen-nr-${s.id}`} />
      <input value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Notiz (optional)" className="w-40 h-7 px-2 border border-gray-300 rounded-md" data-testid={`cobrowse-zuordnen-notiz-${s.id}`} />
      <button type="button" onClick={speichern} className="h-7 px-2.5 rounded-md bg-[#1B3A5C] text-white" data-testid={`cobrowse-zuordnen-speichern-${s.id}`}>OK</button>
      <button type="button" onClick={() => setOffen(false)} className="text-gray-400" aria-label="Abbrechen"><X className="w-3.5 h-3.5" /></button>
    </span>
  );
};

const Protokoll = ({ liste, onChanged }) => (
  <div className="bg-white border border-gray-200 p-5" data-testid="cobrowse-protokoll">
    <h3 className="text-[10px] tracking-[0.2em] uppercase text-[#b8893d] flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Protokoll</h3>
    {!liste.length ? <p className="text-xs text-gray-500 mt-2">Noch keine Sitzungen.</p> : (
      <div className="overflow-x-auto mt-3"><table className="w-full text-xs min-w-[760px]">
        <thead><tr className="text-left text-gray-400 uppercase tracking-wider text-[10px]"><th className="py-1.5">Beginn</th><th>Mandant</th><th>Mitarbeiter</th><th>Herkunft</th><th>Dauer</th><th>Status</th><th>Seiten</th><th>Mandant / Notiz</th></tr></thead>
        <tbody>{liste.map((s) => (
          <tr key={s.id} className="border-t border-gray-100 text-[#34536b]" data-testid={`cobrowse-protokoll-${s.id}`}>
            <td className="py-2">{fmt(s.joined_at || s.created_at)}</td><td>{s.client_name}</td><td>{s.agent_name || '—'}</td><td>{s.herkunft}</td>
            <td>{dauer(s.joined_at, s.ended_at)}</td><td>{STATUS[s.status] || s.status}{s.ended_by ? ` (${s.ended_by})` : ''}</td>
            <td className="max-w-[220px] truncate" title={(s.seiten || []).join(' → ')}>{(s.seiten || []).length}</td>
            <td className="py-2"><Zuordnung s={s} onDone={onChanged} />{s.notiz && <span className="block text-[10px] text-gray-500 max-w-[220px] truncate" title={s.notiz}>{s.notiz}</span>}</td>
          </tr>))}</tbody>
      </table></div>
    )}
  </div>
);

const SCHRITTE = [
  ['1', 'Mandant startet', 'Am Telefon: „Klicken Sie unten links auf Bildschirm teilen.“ Auf der Website oder im Portal, ohne Installation.'],
  ['2', 'Code nennen lassen', 'Der Mandant stimmt zu und nennt Ihnen den 6-stelligen Code. Ist er eingeloggt, können Sie ihn alternativ rechts einladen.'],
  ['3', 'Live helfen', 'Sie sehen seinen Tab und können klicken, scrollen und Felder ausfüllen. Beenden können Sie und der Mandant jederzeit.'],
];
const Ablauf = () => (
  <ol className="grid md:grid-cols-3 gap-3" data-testid="cobrowse-ablauf">
    {SCHRITTE.map(([n, t, x]) => (
      <li key={n} className="relative rounded-2xl bg-[#0B1724] text-white p-5 overflow-hidden">
        <span className="absolute -right-2 -top-4 font-serif text-7xl text-white/5 select-none">{n}</span>
        <span className="inline-flex w-7 h-7 rounded-full bg-[#b8893d] text-[#0B1724] text-xs font-bold items-center justify-center">{n}</span>
        <p className="font-serif text-xl mt-3">{t}</p>
        <p className="text-xs text-white/65 mt-1 leading-relaxed">{x}</p>
      </li>))}
  </ol>
);

export default function KanzleiCobrowse() {
  const { state } = useLocation();
  const [sitzung, setSitzung] = useState(state?.sitzung || null);
  const [liste, setListe] = useState([]);
  const laden = useCallback(() => api.get('/cobrowse/protokoll').then((r) => setListe(r.data)).catch(() => {}), []);
  useEffect(() => { laden(); }, [laden, sitzung]);
  const ende = useCallback(() => setSitzung(null), []);
  return (
    <div className="min-h-screen bg-[#f5f8fa]" data-testid="kanzlei-cobrowse">
      <Header />
      <div className="max-w-[1500px] mx-auto px-4 lg:px-8 pt-[124px] lg:pt-[172px] pb-16 space-y-6">
        <header>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#b8893d]"><MonitorUp className="w-4 h-4" /> Co-Browsing</div>
          <h1 className="font-serif text-4xl text-[#1B3A5C] mt-1">Gemeinsam auf den Bildschirm</h1>
          <p className="text-sm text-[#34536b] mt-1 max-w-2xl">Sehen Sie den Browser-Tab des Mandanten live und bedienen Sie ihn für ihn. Die Übertragung läuft ausschließlich über den Kanzlei-Server, Passwörter, Bank- und Steuerdaten sind geschwärzt.</p>
        </header>
        {!sitzung && <Ablauf />}
        {sitzung ? <Ansicht sitzung={sitzung} onEnde={ende} /> : (
          <div className="grid lg:grid-cols-2 gap-6"><CodeFormular onStart={setSitzung} /><Einladen onStart={setSitzung} /></div>
        )}
        <Protokoll liste={liste} onChanged={laden} />
      </div>
      <Toaster />
    </div>
  );
}
